import classNames from 'classnames';
import { useEffect, useMemo, useRef, useState } from 'react';

import { Icon, IconPlus } from '@flixbus/honeycomb-icons-react';

import { MODAL_NAMES } from 'common/activeElements/modals/modalNames';
import { getExtraActivityIcon } from 'common/ActivityChip/utils/extraActivities';
import AnchoredPortalLayer from 'common/AnchoredPortalLayer';
import { EXTRA_ACTIVITY_MANAGEMENT_ENABLED } from 'constants/featureFlags';
import { useActiveElements } from 'global/ActiveElements/useActiveElements';

import { subscribeExtraActivityTimelineManagementClose } from './events';
import {
	EXTRA_ACTIVITY_TIMELINE_SIDES,
	buildTimelineExtraActivityPayload,
	getTimelineExtraActivityStationCode,
	getTimelineExtraActivityOptions,
} from './rules';

import styles from './styles.module.css';

const SIDE_LABELS = {
	[EXTRA_ACTIVITY_TIMELINE_SIDES.before]: 'before',
	[EXTRA_ACTIVITY_TIMELINE_SIDES.after]: 'after',
};

const HIDE_DELAY_MS = 120;

const getManagementLayerCoords = ({ anchorRect }) => ({
	top: anchorRect.top,
	left: anchorRect.left,
	width: anchorRect.width,
	height: anchorRect.height,
});

let extraActivityManagementInstanceCounter = 0;

const createExtraActivityManagementInstanceId = () => {
	extraActivityManagementInstanceCounter += 1;

	return `extra-activity-timeline-management-${extraActivityManagementInstanceCounter}`;
};

const ExtraActivityTimelineManagement = ({
	anchorRef,
	record,
	isStartInTimeWindow = true,
	isEndInTimeWindow = true,
	isVisible = true,
	onInstanceIdChange,
	onInteractionActiveChange,
}) => {
	const active = useActiveElements();
	const fallbackAnchorRef = useRef(null);
	const instanceIdRef = useRef(null);
	const [activeSide, setActiveSide] = useState(null);
	const [isLayerHovered, setIsLayerHovered] = useState(false);
	const [isLayerFocused, setIsLayerFocused] = useState(false);
	const [isLayerVisible, setIsLayerVisible] = useState(isVisible);

	if (!instanceIdRef.current) {
		instanceIdRef.current = createExtraActivityManagementInstanceId();
	}

	const optionsBySide = useMemo(
		() => ({
			[EXTRA_ACTIVITY_TIMELINE_SIDES.before]: isStartInTimeWindow
				? getTimelineExtraActivityOptions(record, EXTRA_ACTIVITY_TIMELINE_SIDES.before)
				: [],
			[EXTRA_ACTIVITY_TIMELINE_SIDES.after]: isEndInTimeWindow
				? getTimelineExtraActivityOptions(record, EXTRA_ACTIVITY_TIMELINE_SIDES.after)
				: [],
		}),
		[isEndInTimeWindow, isStartInTimeWindow, record]
	);

	const visibleSides = Object.values(EXTRA_ACTIVITY_TIMELINE_SIDES).filter(
		(side) => optionsBySide[side].length > 0
	);
	const layerAnchorRef = anchorRef ?? fallbackAnchorRef;
	const shouldKeepLayerVisible =
		isVisible || isLayerHovered || isLayerFocused || Boolean(activeSide);
	const isInteractionActive =
		isLayerVisible || isVisible || isLayerHovered || isLayerFocused || Boolean(activeSide);

	useEffect(() => {
		onInstanceIdChange?.(instanceIdRef.current);

		return () => onInstanceIdChange?.(null);
	}, [onInstanceIdChange]);

	useEffect(() => {
		onInteractionActiveChange?.(isInteractionActive);
	}, [isInteractionActive, onInteractionActiveChange]);

	useEffect(() => {
		return () => onInteractionActiveChange?.(false);
	}, [onInteractionActiveChange]);

	useEffect(
		() =>
			subscribeExtraActivityTimelineManagementClose(({ sourceId }) => {
				if (sourceId === instanceIdRef.current) return;

				setActiveSide(null);
				setIsLayerFocused(false);
				setIsLayerHovered(false);
				setIsLayerVisible(false);
			}),
		[]
	);

	useEffect(() => {
		if (shouldKeepLayerVisible) {
			setIsLayerVisible(true);
			return undefined;
		}

		const timeoutId = window.setTimeout(() => {
			setIsLayerVisible(false);
		}, HIDE_DELAY_MS);

		return () => window.clearTimeout(timeoutId);
	}, [shouldKeepLayerVisible]);

	if (!EXTRA_ACTIVITY_MANAGEMENT_ENABLED) return null;
	if (visibleSides.length === 0) return null;

	const handlePlusClick = (event, side) => {
		event.preventDefault();
		event.stopPropagation();
		setActiveSide((currentSide) => (currentSide === side ? null : side));
	};

	const handleOptionClick = (event, side, activityType) => {
		event.preventDefault();
		event.stopPropagation();
		active.setActiveElement(
			MODAL_NAMES.manageExtraActivity,
			buildTimelineExtraActivityPayload({ record, side, activityType })
		);
		setActiveSide(null);
		setIsLayerVisible(false);
	};

	return (
		<div
			ref={fallbackAnchorRef}
			className={styles.management}
			data-testid="extra-activity-management-anchor"
			onClick={(event) => event.stopPropagation()}
		>
			<AnchoredPortalLayer
				anchorRef={layerAnchorRef}
				isOpen={isLayerVisible}
				className={styles.portal}
				closeOnOutsidePointerDown={Boolean(activeSide)}
				closeOnEscape={Boolean(activeSide)}
				data-testid="extra-activity-management-portal"
				getCoords={getManagementLayerCoords}
				onBlur={(event) => {
					if (!event.currentTarget.contains(event.relatedTarget)) {
						setIsLayerFocused(false);
					}
				}}
				onClick={(event) => event.stopPropagation()}
				onClose={() => setActiveSide(null)}
				onFocus={() => setIsLayerFocused(true)}
				onPointerEnter={() => setIsLayerHovered(true)}
				onPointerLeave={() => setIsLayerHovered(false)}
			>
				<div className={styles.layer}>
					{visibleSides.map((side) => (
						<button
							key={side}
							type="button"
							className={classNames(styles.plusButton, styles[side])}
							aria-label={`Add extra activity ${SIDE_LABELS[side]}`}
							aria-haspopup="menu"
							aria-expanded={activeSide === side}
							onClick={(event) => handlePlusClick(event, side)}
						>
							<Icon InlineIcon={IconPlus} size={4} />
						</button>
					))}

					{activeSide && (
						<div
							role="menu"
							className={classNames(styles.menu, styles[activeSide])}
							onClick={(event) => event.stopPropagation()}
						>
							<div className={styles.stationCode}>
								{getTimelineExtraActivityStationCode(record, activeSide)}
							</div>
							{optionsBySide[activeSide].map(({ type, label }) => (
								<button
									key={type}
									type="button"
									role="menuitem"
									className={styles.menuItem}
									onClick={(event) => handleOptionClick(event, activeSide, type)}
								>
									<span className={styles.menuIcon}>{getExtraActivityIcon(type)}</span>
									<span>{label}</span>
								</button>
							))}
						</div>
					)}
				</div>
			</AnchoredPortalLayer>
		</div>
	);
};

export default ExtraActivityTimelineManagement;
